import {
  Button,
  Callout,
  Classes,
  IconSize,
  Intent,
  PopoverInteractionKind,
  PopoverPosition,
  Spinner,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { View } from '@carbon/icons-react';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { emptyArray } from 'vet-bones/constants';
import { OutlinedIconButton } from 'vet-bones/design-system/components';
import { isEqual } from 'vet-bones/utils';
import { SelectItem, createSelectItem } from 'vet-bones/utils/select';

import { UNKNOWN_DATA_SOURCE_TYPE } from 'src/constants/dataSourceTypeDescription';
import { ProjectResourceType } from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import { CollapsibleTable } from 'src/containers/ProjectDiagram/components/resourceDrawer/CollapsibleTable';
import { FilterableCollapsibleTable } from 'src/containers/ProjectDiagram/components/resourceDrawer/FilterableCollapsibleTable';
import {
  LinkTableCell,
  TableColumn,
} from 'src/containers/ProjectDiagram/components/resourceDrawer/TableItemRenderer';
import {
  LinkLabelCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import {
  useAddResourceMapping,
  useOldAddResourceMapping,
} from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddResourceMapping';
import { useClickModelConceptClassLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptClassLink';
import { useClickModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConceptRelationshipLink';
import { useRemoveResource } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResource';
import { useRemoveResourceMapping } from 'src/containers/ProjectDiagram/hooks/remove/useRemoveResourceMapping';
import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { NodeData } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptClass,
  isModelConceptRelationshipLabel,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceLabelsByTypeSelector } from 'src/selectors/dataSourceLabelsByType';
import { entityDrawerNodeDataSelector } from 'src/selectors/entityDrawerNodeData';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';
import { projectResourceSelector } from 'src/selectors/projectResource';
import { projectVirtualGraphSelector } from 'src/selectors/projectVirtualGraph';
import {
  ProjectResourceField,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { formatDateFromMs } from 'src/utils/formatDateFromMs';
import { stopPropagation } from 'src/utils/handlers/stopPropagation';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { usePortalConnection } from 'src/utils/hooks/usePortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ResourceFieldItem = SelectItem & ProjectResourceField;

type MappingItem = SelectItem &
  Mapping & {
    target: string;
    targetClassItem?: SelectItem;
    targetRelationshipItem?: SelectItem;
  };

export const ProjectDiagramResourceDrawer: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();

  const nodeData = useReduxSelector(
    entityDrawerNodeDataSelector(projectId, isResource),
    isEqual
  );
  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );
  const nodeDataArray = useReduxSelector(
    projectNodeDataArraySelector(projectId),
    isEqual
  );
  const linkDataArray = useReduxSelector(
    projectLinkDataArraySelector(projectId),
    isEqual
  );

  // connect to get stardog version for this resource
  const connection = useReduxSelector(connectionSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const virtualGraph = useReduxSelector(
    projectVirtualGraphSelector(projectId, nodeData?.value)
  );
  const isAutoMappingSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isAutoMappingSupported', connection)
  );
  usePortalConnection({
    connectionId: virtualGraph?.connectionId,
    key: nodeData?.value,
  });

  const projectResource = useReduxSelector(
    projectResourceSelector(projectId, nodeData?.value, nodeData?.type)
  );
  const dataSourceLabelsByType = useReduxSelector(
    dataSourceLabelsByTypeSelector(connection)
  );

  const getTarget = React.useCallback(
    (key: string | undefined) => {
      const nodeData = nodeDataArray.find(
        (nodeData: NodeData) =>
          (isModelConceptClass(nodeData) ||
            isModelConceptRelationshipLabel(nodeData)) &&
          nodeData.key === key
      );
      const target = getLabelForIri(labelsByIri, nodeData?.value || null);
      if (nodeData?.category === NodeCategory.MODEL_CONCEPT_CLASS) {
        return {
          target,
          targetClassItem: createSelectItem('', nodeData.value),
        };
      }
      if (
        nodeData?.category ===
        LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL
      ) {
        return {
          target,
          targetRelationshipItem: createSelectItem('', nodeData.value),
        };
      }
      return null;
    },
    [labelsByIri, nodeDataArray]
  );

  const handleAddMapping = useAddResourceMapping(connection);
  const handleOldAddMapping = useOldAddResourceMapping();
  const handleMappingClicked = useNavigateMapping();
  const handleClickClass = useClickModelConceptClassLink();
  const handleClickRelationship = useClickModelConceptRelationshipLink();
  const handleRemoveMapping = useRemoveResourceMapping();
  const handleRemoveResource = useRemoveResource();

  const nodeDataType = nodeData?.type;

  const typeText = React.useMemo(() => {
    const resource = projectResource as ProjectVirtualGraph | null;
    const resourceDataType =
      (resource && resource.dataSourceType) || UNKNOWN_DATA_SOURCE_TYPE;
    switch (nodeDataType) {
      case ProjectResourceType.FILE_UPLOAD:
        return 'CSV';
      case ProjectResourceType.VIRTUAL_GRAPH:
        return (
          dataSourceLabelsByType[resourceDataType] ||
          dataSourceLabelsByType[UNKNOWN_DATA_SOURCE_TYPE]
        );
      default:
        return '';
    }
  }, [dataSourceLabelsByType, nodeDataType, projectResource]);

  const fieldItems = React.useMemo<ResourceFieldItem[]>(() => {
    if (!nodeData || !projectResource) {
      return emptyArray as any;
    }

    return (projectResource?.fields as ProjectResourceField[]).map((field) => ({
      text: field.label,
      value: field.value,
    }));
  }, [nodeData, projectResource]);

  const mappingItems = React.useMemo<MappingItem[]>(() => {
    if (!nodeData || !linkDataArray.length || !projectResource) {
      return emptyArray as any;
    }

    const mappingDataArray = linkDataArray.filter(
      (linkData): linkData is Mapping =>
        isMapping(linkData) && linkData.from === nodeData.key
    );

    return mappingDataArray.map((mappingData) => ({
      key: mappingData.key,
      text: mappingData.text,
      value: mappingData.value,
      ...getTarget(mappingData.to),
    }));
  }, [linkDataArray, nodeData, projectResource, getTarget]);

  if (!nodeData || !projectResource) {
    return null;
  }

  return (
    <>
      <div className={Classes.DRAWER_BODY}>
        <Callout
          className="sd-drawer-resource-details"
          data-testid={TEST_IDS.projectDiagram.resourceDrawer.details}
        >
          <div>
            {projectResource.name}{' '}
            <Tooltip
              content={copy.components.project.resources.viewPreview}
              interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
              position={PopoverPosition.RIGHT}
            >
              <OutlinedIconButton
                leftIcon={View}
                onClick={() => {
                  if (nodeDataType === ProjectResourceType.FILE_UPLOAD) {
                    dispatch(
                      uiSlice.actions.toggledFileUploadPreviewDialog({
                        isOpen: true,
                        resourceId: nodeData.value,
                      })
                    );
                  } else if (
                    nodeDataType === ProjectResourceType.VIRTUAL_GRAPH
                  ) {
                    const virtualGraphId = projectResource.id;
                    if (virtualGraphId) {
                      dispatch(
                        uiSlice.actions.toggledVirtualGraphPreviewDialog({
                          isOpen: true,
                          resourceId: nodeData.value,
                        })
                      );
                    }
                  }
                }}
                testId={TEST_IDS.projectDiagram.resourceDrawer.preview}
              />
            </Tooltip>
          </div>
          <div className={Classes.TEXT_SMALL}>
            {copy.components.projectDiagram.resourceDrawer.updateDate}:
          </div>
          <div
            className={Classes.TEXT_SMALL}
            data-testid={TEST_IDS.projectDiagram.resourceDrawer.type}
          >
            {copy.components.projectDiagram.resourceDrawer.type}: {typeText}
          </div>
          <div className={Classes.TEXT_SMALL}>
            {formatDateFromMs(projectResource.dateLastUpdated)}
          </div>
        </Callout>

        <FilterableCollapsibleTable
          header={copy.components.projectDiagram.resourceDrawer.fields}
          items={fieldItems}
          testId={TEST_IDS.projectDiagram.resourceDrawer.fields}
        >
          <TableColumn
            itemKey="text"
            name={copy.components.projectDiagram.resourceDrawer.fieldName}
          />
        </FilterableCollapsibleTable>

        <CollapsibleTable
          actions={
            <Button
              data-testid={TEST_IDS.projectDiagram.resourceDrawer.mapping.add}
              disabled={isConnecting}
              icon={
                isConnecting ? (
                  <Spinner intent={Intent.PRIMARY} size={IconSize.STANDARD} />
                ) : (
                  IconNames.NEW_LINK
                )
              }
              intent={Intent.PRIMARY}
              onClick={() => {
                if (isAutoMappingSupported) {
                  handleAddMapping({ initialResourceIds: [nodeData.value] });
                } else {
                  handleOldAddMapping();
                }
              }}
              outlined
              small
              text={copy.components.projectDiagram.resourceDrawer.newMapping}
            />
          }
          hasHiddenUntilHover
          header={copy.components.projectDiagram.resourceDrawer.mapping}
          items={mappingItems}
          testId={TEST_IDS.projectDiagram.resourceDrawer.mapping.container}
        >
          <TableColumn<MappingItem>
            itemKey="text"
            name={copy.components.projectDiagram.resourceDrawer.mappingName}
            onClick={({ item }) => handleMappingClicked(item.value)}
            TableCell={LinkTableCell}
          />
          <TableColumn<MappingItem>
            itemKey="target"
            name={copy.components.projectDiagram.resourceDrawer.target}
            onClick={({ item }) => {
              if (item.targetClassItem) {
                handleClickClass(item.targetClassItem);
              } else if (item.targetRelationshipItem) {
                handleClickRelationship(item.targetRelationshipItem);
              }
            }}
            TableCell={LinkTableCell}
          />
          <TableColumn<MappingItem>
            TableCell={({ item }) => (
              <td
                key={`${item.value}-remove`}
                className="sd-hidden-until-hover"
              >
                <Tooltip
                  content={
                    copy.components.projectDiagram.resourceDrawer.removeMapping
                  }
                  usePortal={false}
                >
                  <Button
                    minimal
                    onClick={(event) => {
                      stopPropagation(event);
                      handleRemoveMapping(item.key);
                    }}
                    rightIcon={IconNames.TRASH}
                  />
                </Tooltip>
              </td>
            )}
          />
        </CollapsibleTable>
      </div>

      <div className={Classes.DRAWER_FOOTER}>
        <div className="sd-drawer-footer-actions">
          <Button
            data-testid={TEST_IDS.projectDiagram.entityDrawer.removeButton}
            onClick={() => handleRemoveResource({ key: nodeData.key })}
            outlined
          >
            {copy.components.projectDiagram.entityDrawerButtons.remove}
          </Button>
        </div>
      </div>
    </>
  );
};
